
import React from 'react';
import { useProducts } from '@/hooks/useProducts';
import ProductsGrid from './ProductsGrid';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import TranslatedText from '@/TranslatedText';
import { topCategoriesData } from './topCategoriesData';

// Define a consistent Product interface
interface Product {
  id: string;
  name: string;
  price: number;
  currency: string;
  image: string;
  description: string;
  discount?: number;
  couponCode?: string;
  category?: string; // Ensure category field is included
}

// Helper function to convert API products to the format needed by ProductsGrid
const formatProducts = (apiProducts: any[]): Product[] => {
  return apiProducts.map(product => ({
    id: product.id,
    name: product.name,
    price: product.price,
    currency: 'RWF',
    image: product.imageUrl || product.image,
    description: product.description,
    discount: product.discount,
    couponCode: product.couponCode,
    category: product.category
  }));
};

const TopCategoriesContent: React.FC = () => {
  const { products, isLoading } = useProducts();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  // Group products by category
  const getProductsByCategory = (categoryId: string): Product[] => {
    // Filter products by the category ID
    const filteredProducts = products.filter(product => 
      product.category?.toLowerCase() === categoryId.toLowerCase()
    );
    
    // Format and limit to 5 products
    return formatProducts(filteredProducts).slice(0, 5);
  };

  return (
    <div className="space-y-8">
      {topCategoriesData.map(category => (
        <div key={category.id} className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              {category.icon}
              <TranslatedText text={category.name} />
            </h3>
            <Link to={`/search?category=${category.id}`}>
              <Button variant="outline" size="sm">
                <TranslatedText text="View All" />
              </Button>
            </Link>
          </div>
          <ProductsGrid 
            products={getProductsByCategory(category.id)}
          />
        </div>
      ))}
    </div>
  );
};

export default TopCategoriesContent;
